import { useQueryClient } from "@tanstack/react-query";
import { CircleAlert, CircleCheck, GalleryVerticalEnd, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";

import { ROUTE_PATHS } from "@/shared/constants/route-paths";
import type { UnifiedError } from "@/shared/types/api";
import { Button } from "@/shared/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/ui/card";

import { changeEmailByToken } from "../api";
import { authQueryKeys } from "../query-options";
import { useToken } from "../store";

type Status = "loading" | "success" | "missing" | "invalid" | "failed";

const COPY: Record<Status, { title: string; description: string }> = {
  loading: { title: "正在确认新邮箱", description: "正在处理邮件中的确认链接，请稍候。" },
  success: { title: "邮箱已更换", description: "新邮箱已完成验证，今后可使用新邮箱登录。" },
  missing: { title: "链接不完整", description: "请从新邮箱中打开完整的确认链接。" },
  invalid: { title: "链接已失效", description: "确认链接无效或已过期，请回到账户设置重新发送。" },
  failed: { title: "暂时无法更换邮箱", description: "请求没有完成，请稍后重试。" },
};

export function ChangeEmailContent({ token }: { token: string | null }) {
  const queryClient = useQueryClient();
  const isSignedIn = Boolean(useToken());
  const [status, setStatus] = useState<Status>(token ? "loading" : "missing");
  const [attempt, setAttempt] = useState(0);
  const requestRef = useRef<{ attempt: number; promise: Promise<unknown> } | null>(null);

  useEffect(() => {
    if (!token) return;
    if (requestRef.current?.attempt !== attempt) {
      requestRef.current = { attempt, promise: changeEmailByToken({ token }, { silent: true }) };
    }
    let active = true;
    void requestRef.current.promise.then(() => {
      if (!active) return;
      setStatus("success");
      void queryClient.invalidateQueries({ queryKey: authQueryKeys.userInfo });
    }, (cause: UnifiedError) => {
      if (!active) return;
      setStatus(cause.reason === "TOKEN_INVALID" ? "invalid" : "failed");
    });
    return () => { active = false; };
  }, [attempt, queryClient, token]);

  const { title, description } = COPY[status];
  return (
    <div className="flex w-full max-w-md flex-col gap-6">
      <Link to={ROUTE_PATHS.HOME} className="flex items-center gap-2 self-center font-medium">
        <span className="flex size-6 items-center justify-center rounded-md bg-primary text-primary-foreground">
          <GalleryVerticalEnd className="size-4" />
        </span>
        Pinbox
      </Link>
      <Card role={status === "loading" || status === "success" ? "status" : "alert"}>
        <CardHeader className="justify-items-center text-center">
          {status === "loading" ? <Loader2 className="mb-2 size-9 animate-spin text-muted-foreground" aria-hidden="true" />
            : status === "success" ? <CircleCheck className="mb-2 size-9 text-emerald-600" aria-hidden="true" />
              : <CircleAlert className="mb-2 size-9 text-destructive" aria-hidden="true" />}
          <CardTitle className="text-xl">{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        {status !== "loading" && <CardContent className="flex flex-wrap justify-center gap-2">
          {status === "failed" && <Button type="button" onClick={() => {
            setStatus("loading");
            setAttempt((current) => current + 1);
          }}>重试</Button>}
          <Button asChild variant={status === "success" ? "default" : "outline"}>
            <Link to={isSignedIn ? ROUTE_PATHS.MINE : ROUTE_PATHS.LOGIN}>{isSignedIn ? "返回工作台" : "前往登录"}</Link>
          </Button>
        </CardContent>}
      </Card>
    </div>
  );
}
